import React, { useState, useEffect, useCallback } from 'react';
import {
  Bot, Activity, AlertTriangle, CheckCircle, XCircle, Play, Square, RefreshCw,
  Cpu, MemoryStick, HardDrive, Gauge, Clock, Shield, Zap, BarChart3,
  FileText, Settings, AlertCircle, TrendingUp, Server, Database
} from 'lucide-react';
import { aiAgent } from '../../../services/api/client';
import { toast } from '../../../utils/toastUtils';
import { useI18n } from '../../../i18n';
import AIAgentConfig from './AIAgentConfig';
import AIAgentIncidents from './AIAgentIncidents';
import AIAgentActions from './AIAgentActions';
import AIAgentMetrics from './AIAgentMetrics';
import AIAgentReports from './AIAgentReports';
import AIAgentModels from './AIAgentModels';

const AIAgentDashboard = () => {
  const { t } = useI18n();
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch agent status and config
  const fetchData = useCallback(async () => {
    try {
      const [statusData, configData, metricsData] = await Promise.all([
        aiAgent.getStatus(),
        aiAgent.getConfig(),
        aiAgent.getCurrentMetrics()
      ]);
      setStatus(statusData);
      setConfig(configData);
      setMetrics(metricsData);
    } catch (err) {
      console.error('Failed to fetch AI Agent data:', err);
      toast.error('Erreur lors du chargement des donnees de l\'agent');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Control actions
  const handleStart = async () => {
    setActionLoading(true);
    try {
      await aiAgent.start();
      toast.success('Agent IA demarre');
      await fetchData();
    } catch (err) {
      toast.error('Erreur lors du demarrage');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStop = async () => {
    setActionLoading(true);
    try {
      await aiAgent.stop();
      toast.success('Agent IA arrete');
      await fetchData();
    } catch (err) {
      toast.error('Erreur lors de l\'arret');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestart = async () => {
    setActionLoading(true);
    try {
      await aiAgent.restart();
      toast.success('Agent IA redemarre');
      await fetchData();
    } catch (err) {
      toast.error('Erreur lors du redemarrage');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTriggerAnalysis = async () => {
    setActionLoading(true);
    try {
      await aiAgent.triggerAnalysis();
      toast.success('Analyse declenchee');
    } catch (err) {
      toast.error('Erreur lors de l\'analyse');
    } finally {
      setActionLoading(false);
    }
  };

  const handleHealthCheck = async () => {
    setActionLoading(true);
    try {
      const result = await aiAgent.healthCheck();
      if (result.overall_healthy) {
        toast.success('Tous les services sont operationnels');
      } else {
        toast.warning('Certains services ont des problemes');
      }
      await fetchData();
    } catch (err) {
      toast.error('Erreur lors du health check');
    } finally {
      setActionLoading(false);
    }
  };

  // State badge
  const getStateBadge = (state) => {
    const stateConfig = {
      idle: { color: 'bg-gray-500', text: 'Idle', icon: Clock },
      monitoring: { color: 'bg-green-500', text: 'Monitoring', icon: Activity },
      analyzing: { color: 'bg-blue-500', text: 'Analyzing', icon: Zap },
      recovering: { color: 'bg-orange-500', text: 'Recovering', icon: RefreshCw },
      alerting: { color: 'bg-red-500', text: 'Alert', icon: AlertTriangle },
      stopped: { color: 'bg-gray-700', text: 'Stopped', icon: Square },
      error: { color: 'bg-red-700', text: 'Erreur', icon: XCircle },
    };
    const cfg = stateConfig[state] || stateConfig.idle;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-white ${cfg.color}`}>
        <Icon className="w-4 h-4" />
        {cfg.text}
      </span>
    );
  };

  // Sub-tabs
  const subTabs = [
    { id: 'overview', name: 'Vue d\'ensemble', icon: Activity },
    { id: 'config', name: 'Configuration', icon: Settings },
    { id: 'incidents', name: 'Incidents', icon: AlertTriangle },
    { id: 'actions', name: 'Actions', icon: Zap },
    { id: 'metrics', name: 'Metrics', icon: BarChart3 },
    { id: 'reports', name: 'Reports', icon: FileText },
    { id: 'models', name: 'AI Models', icon: Bot },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-brand-green mb-2">Agent IA Supervisor</h2>
          <p className="text-gray-400">Autonomous monitoring, analysis and automatic recovery</p>
        </div>
        <div className="flex items-center gap-3">
          {status && getStateBadge(status.state)}
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 bg-brand-black-lighter rounded-lg text-gray-400 hover:text-brand-green"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        {status?.state === 'stopped' ? (
          <button
            onClick={handleStart}
            disabled={actionLoading}
            className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30"
          >
            <Play className="w-4 h-4" />
            Start
          </button>
        ) : (
          <button
            onClick={handleStop}
            disabled={actionLoading}
            className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
          >
            <Square className="w-4 h-4" />
            Stop
          </button>
        )}
        <button
          onClick={handleRestart}
          disabled={actionLoading || status?.state === 'stopped'}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 disabled:opacity-50"
        >
          <RefreshCw className="w-4 h-4" />
          Restart
        </button>
        <button
          onClick={handleTriggerAnalysis}
          disabled={actionLoading || status?.state === 'stopped'}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 disabled:opacity-50"
        >
          <Zap className="w-4 h-4" />
          Analyze
        </button>
        <button
          onClick={handleHealthCheck}
          disabled={actionLoading}
          className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 disabled:opacity-50"
        >
          <Shield className="w-4 h-4" />
          Health Check
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-800 pb-4">
        {subTabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeSubTab === tab.id
                  ? 'bg-brand-green text-brand-black font-semibold'
                  : 'text-gray-400 hover:text-brand-green hover:bg-brand-black-lighter'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.name}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {activeSubTab === 'overview' && (
        <div className="space-y-6">
          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Health Status */}
            <div className={`bg-brand-black rounded-xl p-4 border ${status?.is_healthy ? 'border-green-500/30' : 'border-red-500/30'}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${status?.is_healthy ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                  {status?.is_healthy ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Sante</p>
                  <p className={`font-bold ${status?.is_healthy ? 'text-green-400' : 'text-red-400'}`}>
                    {status?.is_healthy ? 'Operationnel' : 'Probleme detecte'}
                  </p>
                </div>
              </div>
            </div>

            {/* Anomalies Detected */}
            <div className="bg-brand-black rounded-xl p-4 border border-orange-500/30">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Anomalies</p>
                  <p className="text-xl font-bold text-orange-400">{status?.anomalies_detected || 0}</p>
                </div>
              </div>
            </div>

            {/* Recoveries */}
            <div className="bg-brand-black rounded-xl p-4 border border-blue-500/30">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Recoveries</p>
                  <p className="text-xl font-bold text-blue-400">
                    {status?.recoveries_successful || 0}/{status?.recoveries_attempted || 0}
                  </p>
                </div>
              </div>
            </div>

            {/* AI Queries */}
            <div className="bg-brand-black rounded-xl p-4 border border-purple-500/30">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Requetes IA</p>
                  <p className="text-xl font-bold text-purple-400">{status?.ai_queries_count || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Current Metrics */}
          {metrics && (
            <div className="bg-brand-black rounded-xl p-6 border border-brand-green/30">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-brand-green" />
                Metriques Actuelles
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* CPU */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 flex items-center gap-2">
                      <Cpu className="w-4 h-4" /> CPU
                    </span>
                    <span className={`font-bold ${metrics.cpu_percent > 80 ? 'text-red-400' : metrics.cpu_percent > 60 ? 'text-orange-400' : 'text-green-400'}`}>
                      {metrics.cpu_percent?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-700 rounded">
                    <div
                      className={`h-full rounded ${metrics.cpu_percent > 80 ? 'bg-red-500' : metrics.cpu_percent > 60 ? 'bg-orange-500' : 'bg-green-500'}`}
                      style={{ width: `${metrics.cpu_percent || 0}%` }}
                    />
                  </div>
                </div>

                {/* Memory */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 flex items-center gap-2">
                      <MemoryStick className="w-4 h-4" /> RAM
                    </span>
                    <span className={`font-bold ${metrics.memory_percent > 80 ? 'text-red-400' : metrics.memory_percent > 60 ? 'text-orange-400' : 'text-green-400'}`}>
                      {metrics.memory_percent?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-700 rounded">
                    <div
                      className={`h-full rounded ${metrics.memory_percent > 80 ? 'bg-red-500' : metrics.memory_percent > 60 ? 'bg-orange-500' : 'bg-green-500'}`}
                      style={{ width: `${metrics.memory_percent || 0}%` }}
                    />
                  </div>
                </div>

                {/* GPU */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 flex items-center gap-2">
                      <Gauge className="w-4 h-4" /> GPU
                    </span>
                    <span className={`font-bold ${metrics.gpu_utilization > 90 ? 'text-red-400' : metrics.gpu_utilization > 70 ? 'text-orange-400' : 'text-green-400'}`}>
                      {metrics.gpu_utilization?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-700 rounded">
                    <div
                      className={`h-full rounded ${metrics.gpu_utilization > 90 ? 'bg-red-500' : metrics.gpu_utilization > 70 ? 'bg-orange-500' : 'bg-green-500'}`}
                      style={{ width: `${metrics.gpu_utilization || 0}%` }}
                    />
                  </div>
                </div>

                {/* Disk */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 flex items-center gap-2">
                      <HardDrive className="w-4 h-4" /> Disque
                    </span>
                    <span className={`font-bold ${metrics.disk_percent > 80 ? 'text-red-400' : metrics.disk_percent > 60 ? 'text-orange-400' : 'text-green-400'}`}>
                      {metrics.disk_percent?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-700 rounded">
                    <div
                      className={`h-full rounded ${metrics.disk_percent > 80 ? 'bg-red-500' : metrics.disk_percent > 60 ? 'bg-orange-500' : 'bg-green-500'}`}
                      style={{ width: `${metrics.disk_percent || 0}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-4 border-t border-gray-800">
                <div className="text-center">
                  <p className="text-gray-500 text-sm">Scans Actifs</p>
                  <p className="text-xl font-bold text-brand-green">{metrics.active_scans || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-500 text-sm">Scans en Attente</p>
                  <p className="text-xl font-bold text-blue-400">{metrics.queued_scans || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-500 text-sm">Erreurs (1h)</p>
                  <p className={`text-xl font-bold ${metrics.error_count_1h > 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {metrics.error_count_1h || 0}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-gray-500 text-sm">Services</p>
                  <p className="text-xl font-bold text-brand-green">
                    {metrics.services_healthy || 0}/{metrics.services_total || 0}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Agent Info */}
          {status && (
            <div className="bg-brand-black rounded-xl p-6 border border-gray-800">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Server className="w-5 h-5 text-brand-green" />
                Informations Agent
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-gray-500 text-sm">{t('settings.uptime')}</p>
                  <p className="text-white font-medium">
                    {status.uptime_seconds ? formatUptime(status.uptime_seconds) : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Dernier Heartbeat</p>
                  <p className="text-white font-medium">
                    {status.last_heartbeat ? new Date(status.last_heartbeat).toLocaleTimeString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Alertes Envoyees</p>
                  <p className="text-white font-medium">{status.alerts_sent || 0}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Erreurs Consecutives</p>
                  <p className={`font-medium ${status.consecutive_errors > 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {status.consecutive_errors || 0}
                  </p>
                </div>
              </div>
              {status.last_error && (
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 inline mr-2" />
                    Last error: {status.last_error}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeSubTab === 'config' && (
        <AIAgentConfig config={config} onUpdate={fetchData} />
      )}

      {activeSubTab === 'incidents' && (
        <AIAgentIncidents />
      )}

      {activeSubTab === 'actions' && (
        <AIAgentActions />
      )}

      {activeSubTab === 'metrics' && (
        <AIAgentMetrics />
      )}

      {activeSubTab === 'reports' && (
        <AIAgentReports />
      )}

      {activeSubTab === 'models' && (
        <AIAgentModels />
      )}
    </div>
  );
};

// Helper function to format uptime
const formatUptime = (seconds) => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (days > 0) {
    return `${days}j ${hours}h ${minutes}m`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

export default AIAgentDashboard;
